import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { AuthService, UserRoles } from '../services/auth.service';
import { CREDIT_ANALYTICS_ROLES } from '../pages/credit-analytics/credit-analytics.access';

/**
 * Factory for role-based route guards.
 * Usage in routing: canActivate: [roleGuard('admin', 'super_admin')]
 */
export function roleGuard(...allowedRoles: string[]): CanActivateFn {
  return () => {
    const authService = inject(AuthService);
    const router = inject(Router);

    const role = authService.accessTokenPayload$.value?.role;
    if (role && allowedRoles.includes(role)) {
      return true;
    }

    router.navigate(['/tutors']);
    return false;
  };
}

/**
 * Guard that asks exactly what the menu asks, so a route can never refuse a
 * page the sidebar is offering.
 *
 * A plain role guard is the wrong tool for these pages: `hasAccess` lets a
 * feature set OVERRIDE role permissions, so `roleGuard('admin', 'manager')`
 * would turn away a `user` whose feature set legitimately grants the page —
 * while the menu still shows it. Routing on the same predicate as the menu is
 * the only way the two stay in step.
 *
 * `hasAccess` is restrictive by construction (no token payload -> false), so
 * this fails closed.
 */
export function accessGuard(userAccess: string): CanActivateFn {
  return () => {
    const authService = inject(AuthService);
    const router = inject(Router);

    if (authService.hasAccess(userAccess)) {
      return true;
    }

    router.navigate(['/tutors']);
    return false;
  };
}


/** Only super_admin can access */
export const SuperAdminGuard = roleGuard(UserRoles.SuperAdmin);

/** Admin routes (super_admin + admin) */
export const AdminGuard = roleGuard(UserRoles.SuperAdmin, UserRoles.Admin);

/** Credit-Verbrauchsseite — Rollen und Freigabe in `credit-analytics.access.ts`. */
export const CreditAnalyticsGuard = roleGuard(...CREDIT_ANALYTICS_ROLES);

/** Dozent routes — mirrors the backend `_authorize_dozent` guard.
 *  Feature sets are RESTRICTIONS on the normal user role, not grants:
 *  a user WITH a feature set needs the coarse 'DOZENT' flag (the Dozent
 *  feature set stores permissions = {DOZENT}, not the granular menu
 *  strings); a user WITHOUT a feature set is a normal user and passes —
 *  the backend scopes non-admins to their own dozents/content. */
export const DozentGuard: CanActivateFn = () => {
  const auth = inject(AuthService);
  const router = inject(Router);
  const payload = auth.accessTokenPayload$.value;
  if (!payload) { router.navigate(['/tutors']); return false; }
  const perms = payload.feature_set_permissions || [];
  if (perms.length === 0 || perms.includes('DOZENT')) return true;
  router.navigate(['/tutors']);
  return false;
};

/** Block plain user role from accessing management routes (users with feature sets CAN access) */
export const NonUserGuard: CanActivateFn = () => {
  const auth = inject(AuthService);
  const router = inject(Router);
  const payload = auth.accessTokenPayload$.value;
  if (!payload) { router.navigate(['/tutors']); return false; }
  // Admin roles always pass
  if ([UserRoles.SuperAdmin, UserRoles.Admin, UserRoles.Manager].includes(payload.role)) return true;
  // Users with feature set pass
  if ((payload.feature_set_permissions || []).length > 0) return true;
  router.navigate(['/tutors']);
  return false;
};
